import { chartPointToPixel } from "./coordinateUtils";
import { renderOverlayPrimitiveToCanvas, } from "@vninvestcharts/engine-core";
import { resolveDrawingStyle } from "./drawingStyleRegistry";
import { toOverlayPrimitive } from "./overlayPrimitiveAdapter";
import { ELLIOTT_LABELS } from "./builtin/elliottWaves";
import { PATTERN_POINT_LABELS } from "./builtin/harmonicPatterns";
import { calculateGannAdvancedGeometry } from "./builtin/gannAdvanced";
import { roundRectPolyfill, numberFormatter } from "./shared";
export const DEFAULT_FIB_LEVELS = [0, 0.236, 0.382, 0.5, 0.618, 0.786, 1, 1.618, 2.618];
export const DEFAULT_FIB_EXTENSION_LEVELS = [1.272, 1.414, 1.618, 2, 2.618];
function strokeDasharrayForStyle(style) {
    switch (style.strokeDasharray) {
        case "dashed":
            return [6, 4];
        case "dotted":
            return [2, 4];
        case "solid":
        case undefined:
        default:
            return [];
    }
}
function applyLineStyle(ctx, style) {
    ctx.strokeStyle = style.stroke;
    ctx.lineWidth = style.strokeWidth;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.setLineDash(strokeDasharrayForStyle(style));
    ctx.globalAlpha = style.opacity ?? 1;
}
function toPixel(point, scales) {
    return chartPointToPixel(point, scales);
}
function drawSelectionHandles(ctx, points, color) {
    if (points.length === 0) {
        return;
    }
    ctx.save();
    ctx.globalAlpha = 1;
    ctx.setLineDash([]);
    ctx.lineWidth = 1;
    points.forEach((point) => {
        ctx.beginPath();
        ctx.arc(point.x, point.y, 4, 0, Math.PI * 2);
        ctx.fillStyle = color;
        ctx.strokeStyle = "#ffffff";
        ctx.fill();
        ctx.stroke();
    });
    ctx.restore();
}
function drawSemanticPointAffordance(ctx, label, position, style, isSelected, isFocused) {
    const fontSize = Math.max(10, Math.min(12, style.fontSize ?? 11));
    ctx.save();
    ctx.globalAlpha = 1;
    ctx.font = `700 ${fontSize}px ${style.fontFamily ?? "sans-serif"}`;
    const textWidth = ctx.measureText(label).width;
    const badgeWidth = Math.max(20, textWidth + 10);
    const badgeHeight = fontSize + 6;
    const badgeX = position.x + 10;
    const badgeY = position.y - badgeHeight - 10;
    const ringRadius = isFocused ? 10 : isSelected ? 8 : 6;
    ctx.fillStyle = isFocused ? "rgba(15, 23, 42, 0.98)" : isSelected ? "rgba(15, 23, 42, 0.94)" : "rgba(15, 23, 42, 0.88)";
    ctx.strokeStyle = style.stroke;
    ctx.lineWidth = isFocused ? 1.5 : 1;
    ctx.beginPath();
    roundRectPolyfill(ctx, badgeX, badgeY, badgeWidth, badgeHeight, 999);
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = "#f8fafc";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(label, badgeX + (badgeWidth / 2), badgeY + (badgeHeight / 2) + 0.5);
    ctx.beginPath();
    ctx.arc(position.x, position.y, ringRadius, 0, Math.PI * 2);
    ctx.fillStyle = style.stroke;
    ctx.globalAlpha = isFocused ? 0.38 : isSelected ? 0.28 : 0.18;
    ctx.fill();
    ctx.globalAlpha = 1;
    ctx.strokeStyle = "#ffffff";
    ctx.lineWidth = isFocused ? 2.5 : isSelected ? 2 : 1.5;
    ctx.stroke();
    ctx.restore();
}
export function resolveSemanticPointLabels(drawing) {
    switch (drawing.type) {
        case "elliottImpulse":
            return drawing.waveLabels ?? ELLIOTT_LABELS.elliottImpulse;
        case "elliottCorrection":
            return drawing.waveLabels ?? ELLIOTT_LABELS.elliottCorrection;
        case "elliottTriangle":
            return drawing.waveLabels ?? ELLIOTT_LABELS.elliottTriangle;
        case "elliottDoubleCombo":
            return drawing.waveLabels ?? ELLIOTT_LABELS.elliottDoubleCombo;
        case "elliottTripleCombo":
            return drawing.waveLabels ?? ELLIOTT_LABELS.elliottTripleCombo;
        case "xabcdPattern":
            return PATTERN_POINT_LABELS.xabcdPattern;
        case "cypherPattern":
            return PATTERN_POINT_LABELS.cypherPattern;
        case "headAndShoulders":
            return PATTERN_POINT_LABELS.headAndShoulders;
        case "trianglePattern":
            return PATTERN_POINT_LABELS.trianglePattern;
        case "threeDrivesPattern":
            return PATTERN_POINT_LABELS.threeDrivesPattern;
        default:
            return null;
    }
}
export function clipSegmentToBox(start, end, chartWidth, chartHeight) {
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    let t0 = 0;
    let t1 = 1;
    const edges = [
        [-dx, start.x],
        [dx, chartWidth - start.x],
        [-dy, start.y],
        [dy, chartHeight - start.y],
    ];
    for (const [p, q] of edges) {
        if (p === 0) {
            if (q < 0) {
                return null;
            }
            continue;
        }
        const r = q / p;
        if (p < 0) {
            if (r > t1) {
                return null;
            }
            if (r > t0) {
                t0 = r;
            }
        }
        else {
            if (r < t0) {
                return null;
            }
            if (r < t1) {
                t1 = r;
            }
        }
    }
    if (t1 < t0) {
        return null;
    }
    return [
        { x: start.x + dx * t0, y: start.y + dy * t0 },
        { x: start.x + dx * t1, y: start.y + dy * t1 },
    ];
}
function extendLineThroughBox(point, direction, chartWidth, chartHeight) {
    return clipSegmentToBox({ x: point.x - direction.x * 10_000, y: point.y - direction.y * 10_000 }, { x: point.x + direction.x * 10_000, y: point.y + direction.y * 10_000 }, chartWidth, chartHeight);
}
function drawLine(ctx, start, end, style) {
    ctx.save();
    applyLineStyle(ctx, style);
    ctx.beginPath();
    ctx.moveTo(start.x, start.y);
    ctx.lineTo(end.x, end.y);
    ctx.stroke();
    ctx.restore();
}
function drawPolyline(ctx, points, style) {
    if (points.length < 2) {
        return;
    }
    ctx.save();
    applyLineStyle(ctx, style);
    ctx.beginPath();
    ctx.moveTo(points[0].x, points[0].y);
    for (let index = 1; index < points.length; index += 1) {
        ctx.lineTo(points[index].x, points[index].y);
    }
    ctx.stroke();
    ctx.restore();
}
function drawTextLabel(ctx, text, position, style, options) {
    ctx.save();
    ctx.globalAlpha = style.opacity ?? 1;
    ctx.fillStyle = options.fillStyle ?? style.stroke;
    ctx.font = `${options.weight ? `${options.weight} ` : ""}${options.fontSize ?? style.fontSize ?? 11}px ${style.fontFamily ?? "sans-serif"}`;
    ctx.textAlign = options.align ?? "left";
    ctx.textBaseline = options.baseline ?? "alphabetic";
    ctx.fillText(text, position.x, position.y);
    ctx.restore();
}
function drawFilledRect(ctx, rect, fill, fillAlpha) {
    ctx.save();
    ctx.globalAlpha = fillAlpha;
    ctx.fillStyle = fill;
    ctx.fillRect(rect.x, rect.y, rect.width, rect.height);
    ctx.restore();
}
function drawStrokedRect(ctx, rect, style) {
    ctx.save();
    applyLineStyle(ctx, style);
    ctx.strokeRect(rect.x, rect.y, rect.width, rect.height);
    ctx.restore();
}
function drawPolygonFill(ctx, points, fill, fillAlpha) {
    if (points.length < 3) {
        return;
    }
    ctx.save();
    ctx.globalAlpha = fillAlpha;
    ctx.fillStyle = fill;
    ctx.beginPath();
    ctx.moveTo(points[0].x, points[0].y);
    for (let index = 1; index < points.length; index += 1) {
        ctx.lineTo(points[index].x, points[index].y);
    }
    ctx.closePath();
    ctx.fill();
    ctx.restore();
}
/** GAP-16: Renders a small filled price tag on the right Y-axis edge */
function drawYAxisPriceTag(ctx, price, y, chartWidth, color) {
    const label = numberFormatter(price);
    const fontSize = 11;
    ctx.save();
    ctx.font = `700 ${fontSize}px sans-serif`;
    const textWidth = ctx.measureText(label).width;
    const padX = 5;
    const padY = 3;
    const tagW = textWidth + padX * 2;
    const tagH = fontSize + padY * 2;
    const tagX = chartWidth - tagW - 1;
    const tagY = y - tagH / 2;
    ctx.fillStyle = color;
    ctx.globalAlpha = 0.9;
    ctx.beginPath();
    roundRectPolyfill(ctx, tagX, tagY, tagW, tagH, 3);
    ctx.fill();
    ctx.globalAlpha = 1;
    ctx.fillStyle = "#ffffff";
    ctx.textAlign = "left";
    ctx.textBaseline = "top";
    ctx.fillText(label, tagX + padX, tagY + padY);
    ctx.restore();
}
export function renderDrawingToCanvas(ctx, drawing, scales, options) {
    if (drawing.visible === false) {
        return;
    }
    // Phase 5.1: hidden drawings are not rendered (distinct from `visible` which is set by CE25 visibility rules)
    if (drawing.hidden === true) {
        return;
    }
    renderDrawingShape(ctx, drawing, scales, options);
    // DT-10-T07: render 🔔 badge if alert is active
    if (drawing.alertCondition?.enabled) {
        renderDrawingAlertIcon(ctx, drawing, scales, options.chartWidth, options.chartHeight);
    }
}
function renderDrawingShape(ctx, drawing, scales, options) {
    const effectiveDrawing = {
        ...drawing,
        style: resolveDrawingStyle(drawing),
    };
    const overlayPrimitive = toOverlayPrimitive(effectiveDrawing, scales, {
        chartWidth: options.chartWidth,
        chartHeight: options.chartHeight,
        plotData: options.plotData,
        focusedPointIndex: options.focusedPointIndex,
    });
    if (overlayPrimitive) {
        renderOverlayPrimitiveToCanvas(ctx, overlayPrimitive, options);
        return;
    }
    switch (effectiveDrawing.type) {
        default:
            return;
    }
    // DT-10-T07: After rendering, draw alert icon if condition is active
    if (drawing.alertCondition?.enabled) {
        renderDrawingAlertIcon(ctx, drawing, scales, options.chartWidth, options.chartHeight);
    }
}
// ─── CE21-DT1: Action handles (delete / clone / lock) ─────────────────────────
/**
 * Compute the bounding box of a drawing's anchor points in pixel space.
 * hLine / vLine are treated as spanning the full chart dimension.
 * (Mirrors getDrawingBoundingBox in hitTest.ts — kept local to avoid circular dep.)
 */
function computeBoundingBox(drawing, scales, chartWidth, chartHeight) {
    if (drawing.points.length === 0) {
        return null;
    }
    if (drawing.type === "hLine" && drawing.points[0]) {
        const p = toPixel(drawing.points[0], scales);
        return { minX: 0, maxX: chartWidth, minY: p.y, maxY: p.y };
    }
    if (drawing.type === "vLine" && drawing.points[0]) {
        const p = toPixel(drawing.points[0], scales);
        return { minX: p.x, maxX: p.x, minY: 0, maxY: chartHeight };
    }
    if (drawing.type === "circle") {
        const [centerPoint, edgePoint] = drawing.points;
        if (!centerPoint || !edgePoint) {
            return null;
        }
        const center = toPixel(centerPoint, scales);
        const edge = toPixel(edgePoint, scales);
        const radius = Math.hypot(edge.x - center.x, edge.y - center.y);
        if (!Number.isFinite(radius) || radius <= 0) {
            return null;
        }
        return {
            minX: center.x - radius,
            maxX: center.x + radius,
            minY: center.y - radius,
            maxY: center.y + radius,
        };
    }
    // For gannSquare/gannBox/gannSquareFixed use actual rendered corners clamped to the
    // visible chart area so action handles are always reachable within the viewport.
    if (drawing.type === "gannSquare" || drawing.type === "gannBox" || drawing.type === "gannSquareFixed") {
        const geo = calculateGannAdvancedGeometry(drawing, scales);
        if (geo) {
            return {
                minX: Math.max(0, Math.min(geo.x1, geo.x2)),
                maxX: Math.min(chartWidth, Math.max(geo.x1, geo.x2)),
                minY: Math.max(0, Math.min(geo.y1, geo.y2)),
                maxY: Math.min(chartHeight, Math.max(geo.y1, geo.y2)),
            };
        }
    }
    const pixels = drawing.points.map((pt) => toPixel(pt, scales));
    if (pixels.length === 0)
        return null;
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    for (const p of pixels) {
        if (p.x < minX)
            minX = p.x;
        if (p.x > maxX)
            maxX = p.x;
        if (p.y < minY)
            minY = p.y;
        if (p.y > maxY)
            maxY = p.y;
    }
    return { minX, maxX, minY, maxY };
}
/**
 * Render the three action handles for a selected drawing onto the canvas.
 *   delete → red circle with × , top-right corner + (14, −14)
 *   clone  → blue circle with +, top-right corner + (32, −14)
 *   lock   → yellow (locked) / gray (unlocked) circle with indicator, top-left + (−14, −14)
 */
export function renderDrawingActionHandles(ctx, drawing, scales, chartWidth = 1000, chartHeight = 600) {
    const bbox = computeBoundingBox(drawing, scales, chartWidth, chartHeight);
    if (!bbox)
        return;
    ctx.save();
    const radius = 8;
    const handles = [
        { x: bbox.maxX + 14, y: bbox.minY - 14, color: "#ff5252", symbol: "\u00d7" },
        { x: bbox.maxX + 32, y: bbox.minY - 14, color: "#2962ff", symbol: "+" },
        { x: bbox.minX - 14, y: bbox.minY - 14, color: drawing.locked ? "#ffc107" : "#9e9e9e", symbol: drawing.locked ? "\ud83d\udd12" : "\ud83d\udd13" },
    ];
    for (const h of handles) {
        ctx.beginPath();
        ctx.arc(h.x, h.y, radius, 0, Math.PI * 2);
        ctx.fillStyle = h.color;
        ctx.fill();
        ctx.strokeStyle = "#fff";
        ctx.lineWidth = 1;
        ctx.stroke();
        ctx.fillStyle = "#fff";
        ctx.font = "bold 12px sans-serif";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(h.symbol, h.x, h.y);
    }
    ctx.restore();
}
/**
 * DT-10-T07: Render a 🔔 bell badge on drawings that have an active alert condition.
 * Drawn at top-right of the drawing's bounding box.
 */
export function renderDrawingAlertIcon(ctx, drawing, scales, chartWidth = 1000, chartHeight = 600) {
    if (!drawing.alertCondition?.enabled)
        return;
    const bbox = computeBoundingBox(drawing, scales, chartWidth, chartHeight);
    if (!bbox)
        return;
    const x = bbox.maxX - 2;
    const y = bbox.minY + 2;
    ctx.save();
    ctx.font = "11px sans-serif";
    ctx.textAlign = "right";
    ctx.textBaseline = "top";
    ctx.globalAlpha = 0.9;
    ctx.fillText("\uD83D\uDD14", x, y);
    ctx.restore();
}
/**
 * Renders the rubber-band selection rectangle.
 * Coordinates are in chart-local pixel space (matching moreProps.mouseXY).
 */
export function renderRubberBand(ctx, startX, startY, currentX, currentY) {
    const x = Math.min(startX, currentX);
    const y = Math.min(startY, currentY);
    const w = Math.abs(currentX - startX);
    const h = Math.abs(currentY - startY);
    if (w < 2 && h < 2) {
        return;
    }
    ctx.save();
    ctx.globalAlpha = 0.08;
    ctx.fillStyle = "#1677ff";
    ctx.fillRect(x, y, w, h);
    ctx.globalAlpha = 1;
    ctx.strokeStyle = "#1677ff";
    ctx.lineWidth = 1;
    ctx.strokeRect(x, y, w, h);
    ctx.restore();
}
/**
 * Render a live measurement tooltip near (px, py).
 * The label is clamped to stay inside the chart viewport.
 */
export function renderMeasurementLabel(ctx, px, py, opts) {
    const PADDING = 6;
    const LINE_HEIGHT = 16;
    const FONT = "12px sans-serif";
    const lines = [opts.priceDeltaText, opts.pctText, opts.barsText];
    ctx.save();
    ctx.font = FONT;
    const maxWidth = Math.max(...lines.map((l) => ctx.measureText(l).width));
    const boxW = maxWidth + PADDING * 2;
    const boxH = lines.length * LINE_HEIGHT + PADDING * 2;
    // Default: just above and to the right of cursor
    let bx = px + 10;
    let by = py - boxH - 6;
    // Clamp right edge
    if (bx + boxW > opts.chartWidth - 4) {
        bx = px - boxW - 10;
    }
    // Clamp bottom edge
    if (by + boxH > opts.chartHeight - 4) {
        by = opts.chartHeight - boxH - 4;
    }
    // Clamp top
    if (by < 4) {
        by = 4;
    }
    // Clamp left
    if (bx < 4) {
        bx = 4;
    }
    // Background
    ctx.globalAlpha = 0.88;
    ctx.fillStyle = "#1a1a2e";
    ctx.beginPath();
    roundRectPolyfill(ctx, bx, by, boxW, boxH, 4);
    ctx.fill();
    ctx.globalAlpha = 1;
    // Text
    ctx.fillStyle = "#ffffff";
    ctx.textBaseline = "top";
    lines.forEach((line, i) => {
        ctx.fillText(line, bx + PADDING, by + PADDING + i * LINE_HEIGHT);
    });
    ctx.restore();
}
